import { useSyncExternalStore } from 'react';

/** Mobile navigation preference: the bottom tab bar or the draggable sheet. */
export type NavMode = 'tab' | 'sheet';

const STORAGE_KEY = 'vm_nav_mode';
/** New users get the bottom tab bar. */
const DEFAULT_NAV_MODE: NavMode = 'tab';

function isNavMode(value: unknown): value is NavMode {
  return value === 'tab' || value === 'sheet';
}

function readStored(): NavMode {
  if (typeof window === 'undefined') return DEFAULT_NAV_MODE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isNavMode(raw) ? raw : DEFAULT_NAV_MODE;
  } catch {
    return DEFAULT_NAV_MODE;
  }
}

// Module-level singleton so every consumer (layout, chat, settings) reads one
// value and re-renders together. Mirrors the useSyncExternalStore store pattern
// used by useGuestAddress; persisted to localStorage so a guest's choice
// survives reloads and a signed-in user's account value is seeded on load.
let currentMode: NavMode = readStored();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): NavMode {
  return currentMode;
}

function getServerSnapshot(): NavMode {
  return DEFAULT_NAV_MODE;
}

function commit(mode: NavMode): void {
  if (mode === currentMode) return;
  currentMode = mode;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Private mode / storage disabled — the in-memory value still drives the UI.
  }
  emit();
}

/** Change the preference. Used directly for guests, and as the immediate local
 * half of a signed-in change (the caller also persists to the account). */
export function setNavMode(mode: NavMode): void {
  commit(mode);
}

/** Apply the account's saved value on load so the account choice wins across
 * devices, without writing anything back to the server. Ignores an
 * absent/unknown server value, leaving the stored or default preference. */
export function applyNavModeFromServer(mode: unknown): void {
  if (isNavMode(mode)) commit(mode);
}

// Cross-tab sync (mirrors useLanguageSync): another tab's change updates this one.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    commit(isNavMode(event.newValue) ? event.newValue : DEFAULT_NAV_MODE);
  });
}

/** Reactive read of the current mobile navigation mode. */
export function useNavMode(): NavMode {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
